<script setup>
import { useId } from "vue";

defineOptions({ inheritAttrs: false });

defineProps({
  modelValue: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});
defineEmits(["update:modelValue"]);

const descriptionId = useId();
</script>

<template>
  <div class="switch-row" :class="{ disabled: disabled }">
    <label class="switch">
      <input
        type="checkbox"
        :checked="modelValue"
        :disabled
        :aria-describedby="$slots['label-info'] ? descriptionId : undefined"
        class="input"
        v-bind="$attrs"
        @change="$emit('update:modelValue', $event.target.checked)"
      />
      <span class="track">
        <span class="knob" />
      </span>
      <span v-if="$slots.default" class="label"><slot /></span>
    </label>
    <slot v-if="$slots['label-info']" :id="descriptionId" name="label-info" />
  </div>
</template>

<style lang="scss" scoped>
@use "@/colors";

// Block-level - see BaseCheckbox.vue for the baseline-shift rationale.
.switch-row {
  position: relative;
  display: flex;
  width: fit-content;
  align-items: center;
  gap: 0.375rem;

  &.disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.switch {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;

  .disabled & {
    cursor: not-allowed;
  }
}

.input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;

  &:focus-visible + .track {
    outline: 2px solid colors.$main;
    outline-offset: 2px;
  }

  &:checked + .track {
    background-color: colors.$main;
  }

  &:checked + .track .knob {
    transform: translateX(16px);
  }
}

.track {
  position: relative;
  width: 36px;
  height: 20px;
  border-radius: 10px;
  background-color: colors.$border-light-color;
  flex-shrink: 0;
  transition: background-color 0.15s ease;
}

.knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: colors.$white;
  transition: transform 0.15s ease;
  box-shadow: 0 1px 3px rgb(0 0 0 / 15%);
}

.label {
  font-size: 0.8125rem;
  color: colors.$text-color;
}
</style>
